import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";
import { useTour } from "./TourContext";

const CARD_WIDTH = 340;
const CARD_GAP = 16;
const HIGHLIGHT_PAD = 8;

/** Read an element's viewport rect + retry until it appears (DOM mounts after nav). */
function useTargetRect(selector, activeKey) {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        // Scroll into view if needed
        const scrollNeeded = r.top < 80 || r.bottom > window.innerHeight - 80;
        if (scrollNeeded) el.scrollIntoView({ behavior: "smooth", block: "center" });
        setRect(el.getBoundingClientRect());
      } else if (attempts < 30) {
        attempts += 1;
        setTimeout(measure, 120);
      } else {
        setRect(null);
      }
    };
    measure();

    const onResize = () => {
      const el = document.querySelector(selector);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [selector, activeKey]);

  return rect;
}

function computeCardPosition(rect, placement) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const estH = 210;
  if (!rect || placement === "center") {
    return {
      left: Math.max(16, vw / 2 - CARD_WIDTH / 2),
      top: Math.max(16, vh / 2 - estH / 2),
      arrow: null,
    };
  }
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  let top, left, arrow = placement;

  switch (placement) {
    case "top":
      top = rect.top - estH - CARD_GAP;
      left = cx - CARD_WIDTH / 2;
      break;
    case "bottom":
      top = rect.bottom + CARD_GAP;
      left = cx - CARD_WIDTH / 2;
      break;
    case "left":
      top = cy - estH / 2;
      left = rect.left - CARD_WIDTH - CARD_GAP;
      break;
    case "right":
    default:
      top = cy - estH / 2;
      left = rect.right + CARD_GAP;
      arrow = "right";
      break;
  }

  // Auto-flip if out of viewport
  if (left + CARD_WIDTH > vw - 16) {
    left = rect.left - CARD_WIDTH - CARD_GAP;
    arrow = "left";
  }
  if (left < 16) {
    left = rect.right + CARD_GAP;
    arrow = "right";
  }
  if (top + estH > vh - 16) top = Math.max(16, rect.top - estH - CARD_GAP);
  if (top < 16) top = rect.bottom + CARD_GAP;

  // Clamp
  left = Math.max(16, Math.min(left, vw - CARD_WIDTH - 16));
  top = Math.max(16, Math.min(top, vh - estH - 16));
  return { top, left, arrow };
}

const Spotlight = ({ rect }) => {
  if (!rect) {
    return <div className="fixed inset-0 z-[60] bg-black/65 backdrop-blur-[1px] pointer-events-auto" />;
  }
  const x = rect.left - HIGHLIGHT_PAD;
  const y = rect.top - HIGHLIGHT_PAD;
  const w = rect.width + HIGHLIGHT_PAD * 2;
  const h = rect.height + HIGHLIGHT_PAD * 2;
  // SVG mask lets us cut a rounded hole AND keep clicks blocked outside the hole.
  return (
    <>
      <svg
        className="fixed inset-0 z-[60] pointer-events-auto"
        width="100%" height="100%"
        style={{ width: "100vw", height: "100vh" }}
        aria-hidden="true"
      >
        <defs>
          <mask id="tour-cutout">
            <rect width="100%" height="100%" fill="white" />
            <rect x={x} y={y} width={w} height={h} rx="12" ry="12" fill="black" />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" mask="url(#tour-cutout)" />
      </svg>
      {/* Animated halo (decorative, non-interactive) */}
      <div
        className="fixed z-[61] rounded-xl pointer-events-none ring-2 ring-secondary/80 animate-tour-pulse"
        style={{ left: x, top: y, width: w, height: h }}
      />
    </>
  );
};

const TourCard = ({ step, stepIdx, total, onNext, onPrev, onClose, rect }) => {
  const pos = computeCardPosition(rect, step.placement);
  const Icon = step.icon || Sparkles;
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === total - 1;

  return (
    <div
      className="fixed z-[70] w-[340px] rounded-2xl border border-border bg-gray-0 p-5 shadow-2xl animate-fade-up"
      style={{ top: pos.top, left: pos.left }}
      data-testid="tour-card"
    >
      <button
        onClick={onClose}
        className="absolute right-3 top-3 text-gray-400 hover:text-gray-700"
        aria-label="Close tour"
        data-testid="tour-close"
      >
        <X className="size-4" />
      </button>
      <div className="flex items-center gap-2.5">
        <div className="flex size-9 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
          <Icon className="size-4" />
        </div>
        <div>
          <div className="iso-label !mb-0">Tour · {stepIdx + 1} of {total}</div>
          <h4 className="font-lexend text-sm font-bold text-gray-900">{step.title}</h4>
        </div>
      </div>
      <p className="mt-3 text-xs leading-relaxed text-gray-600">{step.body}</p>

      {/* Progress */}
      <div className="mt-4 flex gap-1">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i <= stepIdx ? "bg-secondary" : "bg-gray-200"
            }`}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2">
        <button
          onClick={onClose}
          className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-700"
          data-testid="tour-skip"
        >
          Skip tour
        </button>
        <div className="flex items-center gap-1">
          {!isFirst && (
            <button
              onClick={onPrev}
              className="iso-btn-ghost !py-1.5 !text-xs"
              data-testid="tour-prev"
            >
              <ChevronLeft className="size-3.5" /> Back
            </button>
          )}
          <button
            onClick={onNext}
            className="iso-btn-primary !py-1.5 !text-xs"
            data-testid="tour-next"
          >
            {isLast ? (step.cta || "Finish") : (step.cta || "Next")}
            {!isLast && <ChevronRight className="size-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Soft prompt shown on first login asking the user if they want the tour. */
const TourPrompt = ({ onStart, onDismiss }) => (
  <div
    className="fixed bottom-24 right-4 z-[55] w-72 rounded-xl border border-border bg-gray-0 p-4 shadow-card-lg animate-fade-up"
    data-testid="tour-prompt"
  >
    <div className="flex items-start gap-2.5">
      <div className="flex size-8 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
        <Sparkles className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-900">First time here?</div>
        <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
          Take a quick tour to see how to get the most out of GameEdge.
        </p>
      </div>
      <button
        onClick={onDismiss}
        className="text-gray-400 hover:text-gray-700"
        aria-label="Dismiss"
        data-testid="tour-prompt-dismiss"
      >
        <X className="size-3.5" />
      </button>
    </div>
    <div className="mt-3 flex items-center justify-end gap-2">
      <button
        onClick={onDismiss}
        className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-700"
      >
        Maybe later
      </button>
      <button
        onClick={onStart}
        className="iso-btn-primary !py-1.5 !text-xs"
        data-testid="tour-prompt-start"
      >
        <Sparkles className="size-3.5" /> Start tour
      </button>
    </div>
  </div>
);

export const TourOverlay = () => {
  const { active, step, stepIdx, total, next, prev, stop, prompted, start, dismissPrompt } = useTour();
  const rect = useTargetRect(active ? step?.selector : null, `${stepIdx}-${active}`);

  // Lock body scroll while active (overlay handles its own scrolling)
  useEffect(() => {
    if (!active) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, [active]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      {prompted && !active && (
        <TourPrompt
          onStart={() => start(true)}
          onDismiss={dismissPrompt}
        />
      )}
      {active && step && (
        <>
          {/* Clickable dim layer (closes on esc handled below) */}
          <Spotlight rect={step.selector ? rect : null} />
          <TourCard
            step={step}
            stepIdx={stepIdx}
            total={total}
            onNext={next}
            onPrev={prev}
            onClose={() => stop(true)}
            rect={step.selector ? rect : null}
          />
        </>
      )}
    </>,
    document.body
  );
};
